import { DatePipe } from '@angular/common';
import {
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  inject
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { Subscription, interval, lastValueFrom } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
  PageableRequest,
  PaymentRound,
  PaymentRoundControllerService,
  PaymentRoundRunningStatus,
  PaymentRoundSummary
} from 'src/app/admin-api';
import { TableActionButton, TableColumn } from 'src/app/components/table';
import { BillingControlService } from 'src/app/services/billing-control.service';
import { LoaderService } from 'src/app/services/loader.service';
import { PaymentRoundErrorReportComponent } from '../scheduler-jobs/payment-round-error-report/payment-round-error-report.component';
import { PaymentRoundReportModalComponent } from '../scheduler-jobs/payment-round-report-modal/payment-round-report-modal.component';
import { BillingControlCreateRoundModalComponent } from './billing-control-create-round-modal/billing-control-create-round-modal.component';
import { BillingControlGatewayPriorityModalComponent } from './billing-control-gateway-priority-modal/billing-control-gateway-priority-modal.component';
import { BillingControlPaymentPriorityModalComponent } from './billing-control-payment-priority-modal/billing-control-payment-priority-modal.component';

export type BillingHealth = 'healthy' | 'attention' | 'critical' | 'unknown';

export interface FunnelStepCard {
  step: number;
  label: string;
  tip: string;
  count: number;
  tabIndex: number;
}

@Component({
  selector: 'app-billing-control',
  templateUrl: './billing-control.component.html',
  styleUrls: ['./billing-control.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [DatePipe]
})
export class BillingControlComponent implements OnInit, OnDestroy {
  private billingControlService = inject(BillingControlService);
  private paymentRoundApi = inject(PaymentRoundControllerService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private dialogService = inject(DialogService);
  private title = inject(Title);
  private datePipe = inject(DatePipe);

  activeStepIndex = 0;
  paymentRounds: PaymentRound[] = [];
  selectedRoundId: number | null = null;
  selectedSummary: PaymentRoundSummary | null = null;
  runningStatus: PaymentRoundRunningStatus | undefined;
  isActionLoading = false;
  isSummaryLoading = false;
  noTokenCount = 0;
  stepMessageFilter: string | null = null;
  private refreshSub: Subscription | null = null;

  readonly tips = {
    page: 'Painel para criar/iniciar rodadas e acompanhar as pessoas em cada etapa da cobrança.',
    roundsTab:
      'Painel de ações e lista de rodadas — não é uma etapa do funil. Aqui você cria e inicia a cobrança.',
    funnel:
      'Depois de iniciar a cobrança, as pessoas passam por essas etapas nesta ordem. Clique para ver a lista.',
    pipeline:
      'Quantidade real de assinantes acompanhados nesta rodada agora.',
    snapshot:
      'Estimativa gravada quando a rodada foi criada. Pode diferir do total real.',
    approval:
      'Percentual de pessoas com pagamento confirmado entre as já processadas.',
    health:
      'Crítico: pagou e travou depois (ativação/edição). Atenção: há falhas de pagamento ou validação. Saudável: sem alerta pós-pagamento.',
    noToken:
      'Pessoas sem token de cartão válido. Sem isso a cobrança não segue — costuma precisar atualizar o cartão.',
    validation: 'Step 0 VALIDATION: checagens antes de tentar cobrar.',
    payment: 'Step 1 PAYMENT: tentativa de pagamento / aguarda webhook.',
    failed: 'Step 2 FAILED: falha terminal (esgotou gateway/cartão/anual).',
    paid: 'Step 3 PAID: pagamento capturado com sucesso.',
    activation: 'Step 4 ACTIVATION: renovar ou ativar a assinatura.',
    edition: 'Step 5 SUBSCRIBER_EDITION: criar a edição (caixa do mês).',
    finished: 'Step 6 FINISHED: pessoa concluiu o ciclo nesta rodada.'
  };

  paymentRoundCols: TableColumn[] = [
    new TableColumn.Builder()
      .setHeader('Status')
      .setField('roundStatus')
      .setFilter(false)
      .setSortable(false)
      .setInnerHTML((item: PaymentRound) => this.getRoundStatusHtml(item))
      .build(),
    new TableColumn.Builder()
      .setHeader('Rodada')
      .setField('paymentRoundId')
      .setType('number')
      .setCondition('equals')
      .build(),
    new TableColumn.Builder()
      .setHeader('Agendado para')
      .setField('dateScheduled')
      .setType('date')
      .setCondition('between')
      .setDisplayFunction((item: PaymentRound) =>
        item.dateScheduled
          ? this.datePipe.transform(item.dateScheduled, 'dd/MM/yyyy HH:mm') ?? '-'
          : 'Execução manual'
      )
      .build(),
    new TableColumn.Builder()
      .setHeader('Executado em')
      .setField('dateExecuted')
      .setType('date')
      .setCondition('between')
      .setDisplayFunction((item: PaymentRound) =>
        item.dateExecuted
          ? this.datePipe.transform(item.dateExecuted, 'dd/MM/yyyy HH:mm') ?? '-'
          : '-'
      )
      .build(),
    new TableColumn.Builder()
      .setHeader('Criado em')
      .setField('dateCreated')
      .setType('date')
      .setCondition('between')
      .build(),
    new TableColumn.Builder()
      .setHeader('Agendado por')
      .setField('username')
      .setCondition('startsWith')
      .build(),
    new TableColumn.Builder()
      .setHeader('Previsto no início')
      .setField('subscriberCount')
      .setType('formattedInteger')
      .setCondition('equals')
      .build(),
    new TableColumn.Builder()
      .setHeader('Ações')
      .setType('button')
      .setFilter(false)
      .setSortable(false)
      .build()
  ];

  paymentRoundActions: TableActionButton[] = [
    new TableActionButton(
      '',
      (item: PaymentRound) => this.selectRound(item.paymentRoundId ?? null),
      'pi pi-eye',
      () => true,
      '',
      'Analisar esta rodada',
      'bottom',
      true,
      true,
      'secondary'
    ),
    new TableActionButton(
      '',
      (item: PaymentRound, $event?: MouseEvent) =>
        this.confirmDeletePaymentRound(item, $event),
      'pi pi-trash',
      (item: PaymentRound) => !item.dateExecuted,
      '',
      'Excluir rodada',
      'bottom',
      true,
      true,
      'danger'
    )
  ];

  constructor() {
    this.title.setTitle('Controle de Cobrança');
  }

  async ngOnInit(): Promise<void> {
    await Promise.all([this.loadPaymentRounds(), this.loadRunningStatus()]);
    this.pickDefaultRound();
    await this.refreshSelectedSummary();
    this.setupAutoRefresh();
  }

  ngOnDestroy(): void {
    this.refreshSub?.unsubscribe();
  }

  get health(): BillingHealth {
    const s = this.selectedSummary;
    if (!s) {
      return 'unknown';
    }
    if ((s.failureActivation ?? 0) > 0 || (s.failureEdition ?? 0) > 0) {
      return 'critical';
    }
    if ((s.failurePayment ?? 0) > 0 || (s.failureValidation ?? 0) > 0) {
      return 'attention';
    }
    return 'healthy';
  }

  get healthLabel(): string {
    switch (this.health) {
      case 'critical':
        return 'Crítico';
      case 'attention':
        return 'Atenção';
      case 'healthy':
        return 'Saudável';
      default:
        return 'Sem dados';
    }
  }

  get approvalRate(): number {
    const s = this.selectedSummary;
    if (!s) {
      return 0;
    }
    const total =
      (s.totalProcessed || 0) - (s.totalProcessedBenefit || 0);
    const success =
      (s.paymentSuccess || 0) - (s.paymentSuccessBenefit || 0);
    return total > 0 ? Math.round((success / total) * 10000) / 100 : 0;
  }

  get funnelCards(): FunnelStepCard[] {
    const s = this.selectedSummary;
    return [
      {
        step: 0,
        label: 'Validação',
        tip: this.tips.validation,
        count: (s?.waitingValidation ?? 0) + (s?.failureValidation ?? 0),
        tabIndex: 1
      },
      {
        step: 1,
        label: 'Cobrança',
        tip: this.tips.payment,
        count:
          (s?.waitingPayment ?? 0) +
          (s?.pendingPayment ?? 0) +
          (s?.failurePayment ?? 0),
        tabIndex: 2
      },
      {
        step: 2,
        label: 'Falha',
        tip: this.tips.failed,
        count: s?.failurePayment ?? 0,
        tabIndex: 3
      },
      {
        step: 3,
        label: 'Pago',
        tip: this.tips.paid,
        count: s?.paymentSuccess ?? 0,
        tabIndex: 4
      },
      {
        step: 4,
        label: 'Ativação',
        tip: this.tips.activation,
        count: (s?.waitingActivation ?? 0) + (s?.failureActivation ?? 0),
        tabIndex: 5
      },
      {
        step: 5,
        label: 'Edição',
        tip: this.tips.edition,
        count: (s?.waitingEdition ?? 0) + (s?.failureEdition ?? 0),
        tabIndex: 6
      },
      {
        step: 6,
        label: 'Finalizado',
        tip: this.tips.finished,
        count: s?.finished ?? 0,
        tabIndex: 7
      }
    ];
  }

  get roundOptions(): { label: string; value: number }[] {
    return this.paymentRounds
      .filter((r) => r.paymentRoundId != null)
      .map((r) => ({
        label: `#${r.paymentRoundId}${r.dateExecuted ? ' · executada' : r.dateScheduled ? ' · agendada' : ' · manual'}`,
        value: r.paymentRoundId as number
      }));
  }

  async loadPaymentRounds(): Promise<void> {
    LoaderService.showLoader();
    try {
      this.paymentRounds = await lastValueFrom(
        this.billingControlService.listPaymentRounds()
      );
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Não foi possível carregar as rodadas de cobrança.'
      });
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async loadRunningStatus(): Promise<void> {
    try {
      this.runningStatus = await lastValueFrom(
        this.paymentRoundApi
          .getRunningStatus()
          .pipe(map((data) => data.result as PaymentRoundRunningStatus))
      );
    } catch {
      this.runningStatus = undefined;
    }
  }

  async onRoundChange(roundId: number | null): Promise<void> {
    this.selectedRoundId = roundId;
    this.stepMessageFilter = null;
    await this.refreshSelectedSummary();
    this.setupAutoRefresh();
  }

  selectRound(roundId: number | null): void {
    void this.onRoundChange(roundId);
    this.activeStepIndex = 0;
  }

  async refreshAnalysis(): Promise<void> {
    await Promise.all([this.refreshSelectedSummary(), this.loadRunningStatus()]);
  }

  goToStepTab(tabIndex: number, messageFilter: string | null = null): void {
    this.stepMessageFilter = messageFilter;
    this.activeStepIndex = tabIndex;
  }

  openNoTokenView(): void {
    // Força ngOnChanges mesmo se o filtro já era 'token'.
    this.stepMessageFilter = null;
    this.activeStepIndex = 5;
    setTimeout(() => {
      this.stepMessageFilter = 'token';
    });
  }

  async refreshSelectedSummary(): Promise<void> {
    if (!this.selectedRoundId) {
      this.selectedSummary = null;
      this.noTokenCount = 0;
      return;
    }

    this.isSummaryLoading = true;
    try {
      this.selectedSummary = await lastValueFrom(
        this.paymentRoundApi
          .getPaymentRoundSummaryById(this.selectedRoundId)
          .pipe(map((data) => data.result as PaymentRoundSummary))
      );
      await this.loadNoTokenCount();
    } catch {
      this.selectedSummary = null;
      this.noTokenCount = 0;
      this.messageService.add({
        severity: 'warn',
        summary: 'Aviso',
        detail: 'Não foi possível carregar o resumo desta rodada.'
      });
    } finally {
      this.isSummaryLoading = false;
    }
  }

  openReport(): void {
    if (!this.selectedRoundId || !this.selectedSummary) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Selecione uma rodada',
        detail: 'Escolha uma rodada para ver o relatório completo.'
      });
      return;
    }

    this.dialogService.open(PaymentRoundReportModalComponent, {
      data: {
        job: this.selectedSummary,
        loadOnInit: true
      },
      header: `Relatório da rodada ${this.selectedRoundId}`,
      width: '52rem',
      style: { width: 'min(52rem, 95vw)' },
      contentStyle: { overflow: 'auto' }
    });
  }

  openErrorReport(): void {
    if (!this.selectedRoundId) {
      return;
    }

    this.dialogService.open(PaymentRoundErrorReportComponent, {
      data: { paymentRoundId: this.selectedRoundId },
      header: `Detalhes de falhas · Rodada ${this.selectedRoundId}`,
      width: '95vw',
      style: { 'max-width': '1400px' },
      contentStyle: { overflow: 'auto' }
    });
  }

  confirmPause(event: Event): void {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message:
        'Deseja pausar a cobrança em andamento? Novas tentativas param até retomar.',
      icon: 'pi pi-pause',
      acceptLabel: 'Pausar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => void this.pauseServices()
    });
  }

  confirmResume(event: Event): void {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Deseja retomar a cobrança?',
      icon: 'pi pi-play',
      acceptLabel: 'Retomar',
      rejectLabel: 'Cancelar',
      accept: () => void this.resumeServices()
    });
  }

  confirmStartPaymentRound(event: Event): void {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message:
        'Isso inicia a cobrança da rodada: as pessoas entram na Validação e seguem o funil. Continuar?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Iniciar',
      rejectLabel: 'Cancelar',
      accept: () => void this.startPaymentRound()
    });
  }

  openCreatePaymentRoundDialog(): void {
    const dialogRef = this.dialogService.open(BillingControlCreateRoundModalComponent, {
      header: 'Criar rodada',
      width: '42rem',
      style: { width: 'min(42rem, 95vw)' },
      styleClass: 'billing-control-create-round-dialog',
      modal: true,
      dismissableMask: true
    });

    dialogRef.onClose.pipe(take(1)).subscribe((result?: { dateScheduled: string | null }) => {
      if (result === undefined) {
        return;
      }
      void this.createPaymentRound(result.dateScheduled);
    });
  }

  openPaymentPriorityDialog(): void {
    this.dialogService.open(BillingControlPaymentPriorityModalComponent, {
      header: 'Ordem de cobrança por plano',
      width: '44rem',
      style: { width: 'min(44rem, 95vw)' },
      modal: true,
      dismissableMask: true
    });
  }

  openGatewayPriorityDialog(): void {
    this.dialogService.open(BillingControlGatewayPriorityModalComponent, {
      header: 'Ordem de gateway por plano',
      width: '90vw',
      style: { width: 'min(90vw, 80rem)' },
      contentStyle: { overflow: 'auto' },
      modal: true,
      dismissableMask: true
    });
  }

  confirmDeletePaymentRound(item: PaymentRound, event?: MouseEvent): void {
    if (!item.paymentRoundId) {
      return;
    }

    this.confirmationService.confirm({
      target: event?.target as EventTarget,
      message: `Deseja excluir a rodada ${item.paymentRoundId}?`,
      icon: 'pi pi-trash',
      acceptLabel: 'Excluir',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => void this.deletePaymentRound(item.paymentRoundId as number)
    });
  }

  private pickDefaultRound(): void {
    if (this.selectedRoundId != null) {
      return;
    }
    const withId = this.paymentRounds.filter((r) => r.paymentRoundId != null);
    const executed = withId
      .filter((r) => !!r.dateExecuted)
      .sort((a, b) => (b.paymentRoundId as number) - (a.paymentRoundId as number));
    const first = executed[0] ?? withId.sort(
      (a, b) => (b.paymentRoundId as number) - (a.paymentRoundId as number)
    )[0];
    this.selectedRoundId = first?.paymentRoundId ?? null;
  }

  private async loadNoTokenCount(): Promise<void> {
    if (!this.selectedRoundId) {
      this.noTokenCount = 0;
      return;
    }

    try {
      const request: PageableRequest = {
        page: 0,
        pageSize: 1,
        filters: [
          {
            field: 'paymentRoundId',
            value: String(this.selectedRoundId),
            condition: 'equals',
            fieldType: 'number'
          },
          {
            field: 'lastMessage',
            value: 'token',
            condition: 'contains',
            fieldType: 'text'
          }
        ]
      };
      const page = await lastValueFrom(
        this.billingControlService.listSubscriberPaymentSteps(2, request)
      );
      this.noTokenCount = page.totalElements ?? 0;
    } catch {
      this.noTokenCount = 0;
    }
  }

  private setupAutoRefresh(): void {
    this.refreshSub?.unsubscribe();
    this.refreshSub = null;
    if (!this.selectedRoundId || !this.runningStatus?.running) {
      return;
    }
    this.refreshSub = interval(60_000).subscribe(() => {
      void this.refreshSelectedSummary();
      void this.loadRunningStatus();
    });
  }

  private getRoundStatusHtml(item: PaymentRound): string {
    if (item.dateExecuted) {
      return '<span class="billing-control-round-status billing-control-round-status--executed">Executada</span>';
    }
    if (item.dateScheduled) {
      return '<span class="billing-control-round-status billing-control-round-status--scheduled">Agendada</span>';
    }
    return '<span class="billing-control-round-status billing-control-round-status--manual">Manual</span>';
  }

  private async pauseServices(): Promise<void> {
    this.isActionLoading = true;
    LoaderService.showLoader();
    try {
      const message = await lastValueFrom(
        this.paymentRoundApi
          .pausePaymentRound()
          .pipe(map((data) => data.result as string))
      );
      this.messageService.add({
        severity: 'success',
        summary: 'Cobrança pausada',
        detail: message
      });
      await this.loadRunningStatus();
      this.setupAutoRefresh();
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Não foi possível pausar a cobrança.'
      });
    } finally {
      this.isActionLoading = false;
      LoaderService.showLoader(false);
    }
  }

  private async resumeServices(): Promise<void> {
    this.isActionLoading = true;
    LoaderService.showLoader();
    try {
      const message = await lastValueFrom(
        this.paymentRoundApi
          .startPaymentRound()
          .pipe(map((data) => data.result as string))
      );
      this.messageService.add({
        severity: 'success',
        summary: 'Cobrança retomada',
        detail: message
      });
      await this.loadRunningStatus();
      this.setupAutoRefresh();
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Não foi possível retomar a cobrança.'
      });
    } finally {
      this.isActionLoading = false;
      LoaderService.showLoader(false);
    }
  }

  private async startPaymentRound(): Promise<void> {
    if (this.isActionLoading) {
      return;
    }
    this.isActionLoading = true;
    LoaderService.showLoader();
    try {
      const message = await lastValueFrom(this.billingControlService.startPaymentRound());
      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: message
      });
      await this.loadPaymentRounds();
      await this.loadRunningStatus();
      this.pickDefaultRound();
      await this.refreshSelectedSummary();
      this.setupAutoRefresh();
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Não foi possível iniciar a rodada de cobrança.'
      });
    } finally {
      this.isActionLoading = false;
      LoaderService.showLoader(false);
    }
  }

  private async createPaymentRound(dateScheduled: string | null): Promise<void> {
    if (this.isActionLoading) {
      return;
    }
    this.isActionLoading = true;
    LoaderService.showLoader();
    try {
      const created = await lastValueFrom(
        this.billingControlService.createPaymentRound(dateScheduled)
      );
      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: dateScheduled
          ? 'Rodada agendada com sucesso.'
          : 'Rodada criada para execução manual.'
      });
      await this.loadPaymentRounds();
      if (created?.paymentRoundId != null) {
        this.selectedRoundId = created.paymentRoundId;
        await this.refreshSelectedSummary();
      }
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Não foi possível criar a rodada de cobrança.'
      });
    } finally {
      this.isActionLoading = false;
      LoaderService.showLoader(false);
    }
  }

  private async deletePaymentRound(paymentRoundId: number): Promise<void> {
    if (this.isActionLoading) {
      return;
    }
    this.isActionLoading = true;
    LoaderService.showLoader();
    try {
      const message = await lastValueFrom(
        this.billingControlService.deletePaymentRound(paymentRoundId)
      );
      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: message
      });
      if (this.selectedRoundId === paymentRoundId) {
        this.selectedRoundId = null;
        this.selectedSummary = null;
      }
      await this.loadPaymentRounds();
      this.pickDefaultRound();
      await this.refreshSelectedSummary();
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Não foi possível excluir a rodada.'
      });
    } finally {
      this.isActionLoading = false;
      LoaderService.showLoader(false);
    }
  }
}
